import React from "react";
import { Head } from "@inertiajs/react";
import MainLayout from "../../components/MainLayout";
import Container from "../../components/Container";
import { Button } from "@/components/ui/button";
import {
  MessageCircle,
  ChevronDown,
} from "lucide-react";

import { ExclusiveProduct, StoreSettings } from "../../types";

// --- INTERFACES ---
interface Product {
  id: number;
  name: string;
  long_description: string;
  price: string | number;
  images: string[];
  youtube_video_url: string;
  category: { name: string };
  brand: { name: string };
  exclusive_features: Array<{ icon: string; title: string; description: string }>;
  exclusive_reviews: Array<{ comment: string; person: string; profession: string }>;
  faqs: Array<{ question: string; answer: string }>;
}

interface FeaturedProductShowProps {
  product: Product;
  store_settings: StoreSettings;
  exclusive_products: ExclusiveProduct[];
}



// --- SUB-COMPONENTS ---

const HeroSection = ({ product }: { product: Product }) => {
  const [selectedImage, setSelectedImage] = React.useState(
    product.images[0] || "https://picsum.photos/600",
  );

  return (
    <Container className="py-12 md:py-20">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
        <div className="md:col-span-1">
          <div className="aspect-w-1 aspect-h-1 mb-4">
            <img
              src={selectedImage}
              alt={product.name}
              className="w-full h-full object-cover rounded-lg shadow-2xl transform hover:scale-105 transition-transform duration-300"
            />
          </div>
          <div className="grid grid-cols-4 gap-2">
            {product.images.map((image, index) => (
              <div
                key={index}
                className={`aspect-w-1 aspect-h-1 cursor-pointer rounded-md overflow-hidden ${selectedImage === image ? "border-2 border-primary" : ""}`}
                onClick={() => setSelectedImage(image)}
              >
                <img
                  src={image}
                  alt={`${product.name} thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>
        <div className="md:col-span-1 flex flex-col justify-center">
          <span className="text-primary font-semibold mb-2">
            Producto Exclusivo
          </span>
          <h1 className="text-4xl md:text-6xl font-extrabold mb-4 tracking-tighter">
            {product.name}
          </h1>
          <p className="text-lg text-muted-foreground mb-6">
            {product.long_description}
          </p>
          <div className="text-4xl font-bold text-primary mb-8">
            S/. {(Number(product.price) || 0).toFixed(2)}
          </div>
          <Button size="lg" className="w-full md:w-auto text-lg">
            Comprar Ahora
          </Button>
        </div>
      </div>
    </Container>
  );
};

const FeaturesSection = ({ features }: { features: Array<{ icon: string; title: string; description: string }> }) => {
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'Star': return '⭐';
      case 'Shield': return '🛡️';
      case 'Zap': return '⚡';
      case 'Heart': return '❤️';
      case 'Crown': return '👑';
      default: return '⭐';
    }
  };

  return (
    <section className="bg-secondary/50 py-20">
      <Container>
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold">Características Únicas</h2>
          <p className="text-muted-foreground mt-2">
            Descubre por qué este producto es diferente a todo lo demás.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className="bg-background p-6 rounded-lg shadow-md text-center"
            >
              <div className="inline-block p-4 bg-primary/10 text-primary rounded-full mb-4 text-2xl">
                {getIcon(feature.icon)}
              </div>
              <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
              <p className="text-muted-foreground">{feature.description}</p>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
};

const VideoSection = ({ url }: { url: string }) => {
  const getYouTubeEmbedUrl = (videoUrl: string) => {
    if (!videoUrl) return "";
    const videoId = videoUrl.split("v=")[1] || videoUrl.split("/").pop();
    const ampersandPosition = videoId?.indexOf("&");
    if (ampersandPosition !== -1) {
      return `https://www.youtube.com/embed/${videoId?.substring(0, ampersandPosition)}`;
    }
    return `https://www.youtube.com/embed/${videoId}`;
  };

  if (!url) return null;

  return (
    <section className="py-20">
      <Container>
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold">Producto en Acción</h2>
          <p className="text-muted-foreground mt-2">
            Mira cómo nuestro producto cobra vida.
          </p>
        </div>
        <div className="aspect-w-16 max-w-6xl mx-auto">
          <iframe
            src={getYouTubeEmbedUrl(url)}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-80 rounded-lg shadow-2xl"
          ></iframe>
        </div>
      </Container>
    </section>
  );
};

const TestimonialsSection = ({ reviews }: { reviews: Array<{ comment: string; person: string; profession: string }> }) => (
  <section className="bg-secondary/50 py-20">
    <Container>
      <div className="text-center mb-12">
        <h2 className="text-4xl font-bold">Lo que dicen nuestros clientes</h2>
        <p className="text-muted-foreground mt-2">
          La opinión de quienes ya lo tienen.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {reviews.map((review, index) => (
          <div
            key={index}
            className="bg-background p-6 rounded-lg shadow-md flex flex-col"
          >
            <MessageCircle className="w-8 h-8 text-primary/50 mb-4" />
            <p className="text-muted-foreground flex-grow">
              <em>"{review.comment}"</em>
            </p>
            <div className="mt-4 text-right">
              <p className="font-bold">{review.person}</p>
              <p className="text-sm text-muted-foreground">
                {review.profession}
              </p>
            </div>
          </div>
        ))}
      </div>
    </Container>
  </section>
);

const FaqSection = ({ faqs }: { faqs: Array<{ question: string; answer: string }> }) => (
  <section className="py-20">
    <Container>
      <div className="text-center mb-12">
        <h2 className="text-4xl font-bold">Preguntas Frecuentes</h2>
        <p className="text-muted-foreground mt-2">
          Resolvemos tus dudas más comunes.
        </p>
      </div>
      <div className="max-w-3xl mx-auto space-y-4">
        {faqs.map((faq, index) => (
          <details key={index} className="bg-secondary/50 p-4 rounded-lg group">
            <summary className="flex justify-between items-center font-bold cursor-pointer">
              {faq.question}
              <ChevronDown className="w-5 h-5 transition-transform duration-300 group-open:rotate-180" />
            </summary>
            <p className="text-muted-foreground mt-2 pt-2 border-t border-border">
              {faq.answer}
            </p>
          </details>
        ))}
      </div>
    </Container>
  </section>
);

// --- MAIN COMPONENT ---

const FeaturedProductShow = ({
  product,
  store_settings,
  exclusive_products,
}: FeaturedProductShowProps) => {
  return (
    <MainLayout
      store_settings={store_settings}
      exclusiveProducts={exclusive_products}
    >
      <Head title={product.name} />
      <main className="flex-grow">
        <HeroSection product={product} />
        <FeaturesSection features={product.exclusive_features || []} />
        <VideoSection url={product.youtube_video_url} />
        <TestimonialsSection reviews={product.exclusive_reviews || []} />
        <FaqSection faqs={product.faqs || []} />
      </main>
    </MainLayout>
  );
};

export default FeaturedProductShow;
